// Loads the bulk compliance summary and exposes it as a Map keyed by pws_id
// for cheap row-level lookups. The endpoint is slow on cold cache (fans
// out FDEP fetches), so we keep the client-side data fresh for 5 min.

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { fetchSystemsSummary } from "@/api/endpoints";
import type { SystemEnriched } from "@/types/api";

const FIVE_MINUTES = 5 * 60 * 1000;

export interface SummaryView {
  byPwsId: Map<string, SystemEnriched>;
  totalHigh: number;
  totalMedium: number;
  totalLow: number;
  systemsWithHigh: number;
  loaded: boolean;
  loading: boolean;
}

export function useSystemSummary(): SummaryView {
  const query = useQuery({
    queryKey: ["systems", "summary"],
    queryFn: fetchSystemsSummary,
    staleTime: FIVE_MINUTES,
    refetchInterval: FIVE_MINUTES,
  });

  return useMemo<SummaryView>(() => {
    const byPwsId = new Map<string, SystemEnriched>();
    let totalHigh = 0;
    let totalMedium = 0;
    let totalLow = 0;
    let systemsWithHigh = 0;
    for (const e of query.data ?? []) {
      byPwsId.set(e.pws_id, e);
      totalHigh += e.alert_counts.high;
      totalMedium += e.alert_counts.medium;
      totalLow += e.alert_counts.low;
      if (e.alert_counts.high > 0) systemsWithHigh += 1;
    }
    return {
      byPwsId,
      totalHigh,
      totalMedium,
      totalLow,
      systemsWithHigh,
      loaded: query.isSuccess,
      loading: query.isLoading,
    };
  }, [query.data, query.isSuccess, query.isLoading]);
}
